import React, { useState, useEffect, useRef } from "react";
import "./ClockDisplay.css";
import { lang } from "../lang";

function ClockDisplay({
  clock,
  timeFormat,
  clockFontTheme,
  currentGlobalTime,
  onRemove,
  onUpdate,
  availableTimezones,
}) {
  const [currentTimeString, setCurrentTimeString] = useState("");
  const [dayNight, setDayNight] = useState("day");
  const [isEditing, setIsEditing] = useState(false);
  const [isHovered, setIsHovered] = useState(false); // Added missing useState for isHovered

  const [editUserInputName, setEditUserInputName] = useState(
    clock.userInputFriendlyName || ""
  );
  const [editIanaTimezone, setEditIanaTimezone] = useState(clock.ianaTimezone);

  const editFormRef = useRef(null);

  useEffect(() => {
    if (!currentGlobalTime) return;

    try {
      const timeOptions = {
        hour: "numeric",
        minute: "2-digit",
        second: "2-digit",
        timeZone: clock.ianaTimezone,
        hour12: timeFormat === "12h",
      };
      setCurrentTimeString(
        currentGlobalTime.toLocaleString(undefined, timeOptions)
      );

      const formatter = new Intl.DateTimeFormat(undefined, {
        timeZone: clock.ianaTimezone,
        hour: "numeric",
        hour12: false,
      });
      const parts = formatter.formatToParts(currentGlobalTime);
      const hourPart = parts.find((part) => part.type === "hour");
      const currentHourInTimezone = hourPart
        ? parseInt(hourPart.value, 10)
        : new Date(currentGlobalTime).getHours();

      if (currentHourInTimezone >= 6 && currentHourInTimezone < 18) {
        setDayNight("day");
      } else {
        setDayNight("night");
      }
    } catch (error) {
      console.error(
        `Error updating clock for timezone ${clock.ianaTimezone}:`,
        error
      );
      setCurrentTimeString("Invalid Timezone");
      setDayNight("day");
    }
  }, [clock.ianaTimezone, timeFormat, currentGlobalTime]);

  useEffect(() => {
    function handleClickOutside(event) {
      if (
        isEditing &&
        editFormRef.current &&
        !editFormRef.current.contains(event.target) &&
        !event.target.closest(`.edit-clock-button-${clock.id}`)
      ) {
        setIsEditing(false);
      }
    }
    if (isEditing) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isEditing, clock.id]);

  const handleSaveEdit = () => {
    if (onUpdate) {
      onUpdate(
        clock.id,
        clock.isLocal ? clock.ianaTimezone : editIanaTimezone,
        editUserInputName.trim()
      );
    }
    setIsEditing(false);
  };

  const handleEditClick = (e) => {
    e.stopPropagation();
    setEditUserInputName(clock.userInputFriendlyName || "");
    setEditIanaTimezone(clock.ianaTimezone);
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
  };

  const clockTimeClassName = `clock-time ${
    clockFontTheme === "digital" ? "clock-font-digital" : ""
  }`;

  return (
    <div
      className={`clock-display-wrapper ${dayNight} ${
        isEditing ? "editing-active" : ""
      }`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div className="clock-header">
        <span className="clock-timezone-name" title={clock.ianaTimezone}>
          {clock.userInputFriendlyName}
        </span>
        {!clock.isLocal && onRemove && (
          <button
            className="clock-action-button remove-clock-button"
            onClick={() => onRemove(clock.id)}
            title={lang.app.removeClock || "Remove this clock"}
          >
            &times;
          </button>
        )}
      </div>
      <div className={clockTimeClassName}>{currentTimeString}</div>

      <div className="clock-edit-controls" ref={editFormRef}>
        {isHovered && !isEditing && (
          <button
            onClick={handleEditClick}
            className={`clock-action-button edit-timezone-button edit-clock-button-${clock.id}`}
          >
            Edit
          </button>
        )}

        <div className={`edit-form ${isEditing ? "form-visible" : ""}`}>
          <input
            type="text"
            value={editUserInputName}
            onChange={(e) => setEditUserInputName(e.target.value)}
            placeholder="City Name"
            className="clock-city-input-editor"
            onClick={(e) => e.stopPropagation()}
          />
          {!clock.isLocal && (
            <select
              value={editIanaTimezone}
              onChange={(e) => setEditIanaTimezone(e.target.value)}
              onClick={(e) => e.stopPropagation()}
              className="clock-timezone-select"
            >
              {availableTimezones.map((tz) => (
                <option key={tz.name} value={tz.name}>
                  {tz.friendlyName}
                </option>
              ))}
            </select>
          )}
          <div className="edit-form-actions">
            <button
              onClick={handleSaveEdit}
              className="clock-action-button save-timezone-button"
            >
              Save
            </button>
            <button
              onClick={handleCancelEdit}
              className="clock-action-button cancel-edit-button"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ClockDisplay;
